// Modèles de formulaire d'inspection en JSON et brouillons sérialisables (G2).
//
// Ce fichier ne dépend que de Zod : il est partagé tel quel, depuis le package commun, par le serveur (contrôle
// faisant foi) et par l'interface (rendu générique, contrôle immédiat, brouillons). Un brouillon est un objet
// JSON autonome (modèle, version, réponses, version serveur de référence) : la file hors connexion du lot 3
// pourra le chiffrer et le rejouer sans transformation.
//
// Réponses : section -> instance -> champ -> valeur. L'instance vaut « _ » pour une section simple, l'identifiant
// du réservoir pour une section répétée par réservoir, le code produit pour une section répétée par produit.
// Les quantités sont des chaînes décimales (jamais de flottants), les photos des listes d'identifiants de documents.

import { z } from 'zod';

export const TYPES_CHAMPS = ['texte', 'texte_long', 'nombre', 'decimal', 'booleen', 'choix', 'date', 'photo'] as const;
export type TypeChamp = (typeof TYPES_CHAMPS)[number];

export const NATURES_SECTIONS = [
  'releves_stocks',
  'verification_documentaire',
  'equipements',
  'prix_observes',
  'observations',
] as const;
export type NatureSection = (typeof NATURES_SECTIONS)[number];

export const LIBELLES_NATURES_SECTIONS: Record<NatureSection, string> = {
  releves_stocks: 'Relevés de stocks',
  verification_documentaire: 'Vérification documentaire',
  equipements: 'Équipements',
  prix_observes: 'Prix observés',
  observations: 'Observations',
};

export const REPETITIONS = ['aucune', 'reservoirs', 'produits'] as const;
export type Repetition = (typeof REPETITIONS)[number];

const code = z.string().regex(/^[a-z][a-z0-9_]{0,59}$/, { message: 'Code en minuscules, chiffres et soulignés' });

export const schemaChamp = z
  .object({
    code,
    libelle: z.string().trim().min(1).max(200),
    type: z.enum(TYPES_CHAMPS),
    obligatoire: z.boolean().default(false),
    aide: z.string().max(500).optional(),
    unite: z.string().max(20).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    choix: z.array(z.object({ valeur: code, libelle: z.string().min(1).max(100) })).min(2).optional(),
  })
  .refine((c) => c.type !== 'choix' || (c.choix && c.choix.length >= 2), { message: 'Un champ à choix exige au moins deux choix' });
export type Champ = z.infer<typeof schemaChamp>;

export const schemaSection = z.object({
  code,
  titre: z.string().trim().min(1).max(200),
  nature: z.enum(NATURES_SECTIONS),
  repetition: z.enum(REPETITIONS).default('aucune'),
  champs: z.array(schemaChamp).min(1).max(60),
});
export type Section = z.infer<typeof schemaSection>;

export const schemaModeleFormulaire = z
  .object({
    format: z.literal(1),
    sections: z.array(schemaSection).min(1).max(30),
  })
  .superRefine((m, ctx) => {
    const sections = new Set<string>();
    for (const s of m.sections) {
      if (sections.has(s.code)) ctx.addIssue({ code: 'custom', message: `Section en double : ${s.code}` });
      sections.add(s.code);
      const champs = new Set<string>();
      for (const c of s.champs) {
        if (champs.has(c.code)) ctx.addIssue({ code: 'custom', message: `Champ en double dans ${s.code} : ${c.code}` });
        champs.add(c.code);
      }
    }
  });
export type ModeleFormulaire = z.infer<typeof schemaModeleFormulaire>;

export type Valeur = string | number | boolean | string[] | null;
export type Reponses = Record<string, Record<string, Record<string, Valeur>>>;

export const schemaReponses = z.record(
  z.string().max(60),
  z.record(z.string().max(60), z.record(z.string().max(60), z.union([z.string().max(5000), z.number(), z.boolean(), z.array(z.string().max(60)).max(20), z.null()]))),
);

/** Éléments de contexte qui déterminent les instances des sections répétées. */
export interface ContexteFormulaire {
  reservoirs: { id: string; numero: string; produitCode: string }[];
  produits: string[];
}

export const INSTANCE_UNIQUE = '_';

export function instancesSection(section: Section, contexte: ContexteFormulaire): { cle: string; libelle: string }[] {
  if (section.repetition === 'reservoirs') return contexte.reservoirs.map((r) => ({ cle: r.id, libelle: `Réservoir ${r.numero} (${r.produitCode})` }));
  if (section.repetition === 'produits') return contexte.produits.map((p) => ({ cle: p, libelle: p }));
  return [{ cle: INSTANCE_UNIQUE, libelle: section.titre }];
}

export interface ErreurReponse {
  chemin: string;
  message: string;
}

const DECIMAL = /^-?\d{1,15}([.,]\d{1,6})?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function vide(v: Valeur | undefined): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

/** Contrôle d'une valeur selon le type de son champ ; renvoie un motif ou null. */
export function controlerValeur(champ: Champ, v: Valeur): string | null {
  switch (champ.type) {
    case 'texte':
    case 'texte_long':
      if (typeof v !== 'string') return 'Texte attendu';
      if (champ.type === 'texte' && v.length > 300) return '300 caractères au plus';
      return null;
    case 'nombre': {
      if (typeof v !== 'number' || !Number.isInteger(v)) return 'Nombre entier attendu';
      if (champ.min !== undefined && v < champ.min) return `Valeur minimale : ${champ.min}`;
      if (champ.max !== undefined && v > champ.max) return `Valeur maximale : ${champ.max}`;
      return null;
    }
    case 'decimal': {
      if (typeof v !== 'string' || !DECIMAL.test(v)) return 'Nombre décimal attendu (chiffres, séparateur point ou virgule)';
      const n = Number(v.replace(',', '.'));
      if (champ.min !== undefined && n < champ.min) return `Valeur minimale : ${champ.min}`;
      if (champ.max !== undefined && n > champ.max) return `Valeur maximale : ${champ.max}`;
      return null;
    }
    case 'booleen':
      return typeof v === 'boolean' ? null : 'Oui ou non attendu';
    case 'choix':
      return typeof v === 'string' && (champ.choix ?? []).some((c) => c.valeur === v) ? null : 'Choix inconnu';
    case 'date':
      return typeof v === 'string' && DATE.test(v) && !Number.isNaN(Date.parse(v)) ? null : 'Date attendue (AAAA-MM-JJ)';
    case 'photo':
      return Array.isArray(v) && v.every((x) => typeof x === 'string') ? null : 'Liste de photos attendue';
  }
}

/**
 * Contrôle des réponses. En mode brouillon, seules les valeurs saisies sont contrôlées ; en mode complet
 * (transmission), les champs obligatoires de chaque instance doivent être renseignés. Les sections, instances
 * ou champs inconnus du modèle sont refusés.
 */
export function validerReponses(
  modele: ModeleFormulaire,
  reponses: Reponses,
  contexte: ContexteFormulaire,
  mode: 'brouillon' | 'complet',
): ErreurReponse[] {
  const erreurs: ErreurReponse[] = [];
  const sections = new Map(modele.sections.map((s) => [s.code, s]));
  for (const [codeSection, instances] of Object.entries(reponses)) {
    const section = sections.get(codeSection);
    if (!section) {
      erreurs.push({ chemin: codeSection, message: 'Section inconnue du modèle' });
      continue;
    }
    const cles = new Set(instancesSection(section, contexte).map((i) => i.cle));
    for (const [cle, valeurs] of Object.entries(instances)) {
      if (!cles.has(cle)) {
        erreurs.push({ chemin: `${codeSection}.${cle}`, message: 'Instance inconnue (réservoir ou produit hors de l’installation)' });
        continue;
      }
      const champs = new Map(section.champs.map((c) => [c.code, c]));
      for (const [codeChamp, v] of Object.entries(valeurs)) {
        const champ = champs.get(codeChamp);
        if (!champ) {
          erreurs.push({ chemin: `${codeSection}.${cle}.${codeChamp}`, message: 'Champ inconnu du modèle' });
          continue;
        }
        if (vide(v)) continue;
        const motif = controlerValeur(champ, v);
        if (motif) erreurs.push({ chemin: `${codeSection}.${cle}.${codeChamp}`, message: `${champ.libelle} : ${motif}` });
      }
    }
  }
  if (mode === 'complet') {
    for (const section of modele.sections) {
      for (const instance of instancesSection(section, contexte)) {
        for (const champ of section.champs) {
          if (champ.obligatoire && vide(reponses[section.code]?.[instance.cle]?.[champ.code])) {
            erreurs.push({ chemin: `${section.code}.${instance.cle}.${champ.code}`, message: `${champ.libelle} : réponse obligatoire (${instance.libelle})` });
          }
        }
      }
    }
  }
  return erreurs;
}

/** Identifiants des photos citées dans les réponses. */
export function photosCitees(modele: ModeleFormulaire, reponses: Reponses): string[] {
  const ids = new Set<string>();
  for (const s of modele.sections) {
    for (const c of s.champs) {
      if (c.type !== 'photo') continue;
      for (const valeurs of Object.values(reponses[s.code] ?? {})) {
        const v = valeurs[c.code];
        if (Array.isArray(v)) for (const id of v) ids.add(id);
      }
    }
  }
  return [...ids];
}

// Brouillons sérialisables.

export const FORMAT_BROUILLON = 'carburanttrack.brouillon_inspection';

export const schemaBrouillon = z.object({
  format: z.literal(FORMAT_BROUILLON),
  versionFormat: z.literal(1),
  inspectionId: z.string().uuid(),
  formulaireId: z.string().uuid(),
  formulaireVersion: z.number().int().min(1),
  /** Version des réponses connue du serveur au moment du chargement (contrôle de conflit à l'envoi). */
  versionReponses: z.number().int().min(0),
  reponses: schemaReponses,
  modifieLe: z.string().datetime(),
});
export type Brouillon = z.infer<typeof schemaBrouillon>;

export function nouveauBrouillon(p: Omit<Brouillon, 'format' | 'versionFormat' | 'modifieLe'>, maintenant = new Date()): Brouillon {
  return { format: FORMAT_BROUILLON, versionFormat: 1, ...p, modifieLe: maintenant.toISOString() };
}

/** Écrit une valeur dans un brouillon sans le muter (retourne un nouveau brouillon). */
export function modifierBrouillon(b: Brouillon, section: string, instance: string, champ: string, valeur: Valeur, maintenant = new Date()): Brouillon {
  const reponses: Reponses = { ...b.reponses, [section]: { ...(b.reponses[section] ?? {}), [instance]: { ...(b.reponses[section]?.[instance] ?? {}), [champ]: valeur } } };
  return { ...b, reponses, modifieLe: maintenant.toISOString() };
}

export function serialiserBrouillon(b: Brouillon): string {
  return JSON.stringify(schemaBrouillon.parse(b));
}

export function lireBrouillon(texte: string): Brouillon {
  return schemaBrouillon.parse(JSON.parse(texte));
}
